/**
 * Client View: pure helpers (URL, token shape, labels). No data access.
 * Money display uses the shared cent formatter. Amounts themselves come
 * from the Client View payload, which already applied the payment ledger.
 */
import { formatMoneyFromCents, toCents } from "../invoiceMoney";

export const CLIENT_VIEW_PATH_PREFIX = "/c/";
const TOKEN_RE = /^[0-9a-f]{64}$/;

export function isClientViewToken(value) {
  return TOKEN_RE.test(String(value || ""));
}

/** The shareable link. Origin is injectable for tests. */
export function buildClientViewUrl(token, origin = typeof window !== "undefined" ? window.location.origin : "") {
  return `${origin}${CLIENT_VIEW_PATH_PREFIX}${token}`;
}

/**
 * Parse a location pathname. Returns { isClientView, token } where token is
 * null for a /c/… path whose token is malformed (rendered as "link not valid").
 */
export function parseClientViewPath(pathname) {
  const path = String(pathname || "");
  if (!path.startsWith(CLIENT_VIEW_PATH_PREFIX) && path !== "/c") {
    return { isClientView: false, token: null };
  }
  const candidate = path.slice(CLIENT_VIEW_PATH_PREFIX.length).split("/")[0];
  return { isClientView: true, token: isClientViewToken(candidate) ? candidate : null };
}

/** Client-facing status wording. Unknown statuses are title-cased, never raw codes. */
export function clientStatusLabel(status) {
  const key = String(status || "").toLowerCase();
  const known = {
    active: "In progress",
    in_progress: "In progress",
    on_hold: "On hold",
    completed: "Completed",
    complete: "Completed",
    archived: "Closed",
    planning: "Planning",
  };
  if (known[key]) return known[key];
  return key
    ? key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    : "";
}

export function clientStatusTone(status) {
  const label = clientStatusLabel(status);
  if (label === "Completed") return "done";
  if (label === "On hold" || label === "Closed") return "muted";
  return "active";
}

/** Square-foot area for display. */
export function formatArea(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  return `${n.toLocaleString("en-CA", { maximumFractionDigits: 2 })} sq ft`;
}

/** 60 in → 5′ 0″ ; 62.5 → 5′ 2.5″ */
export function formatInches(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  const feet = Math.floor(n / 12);
  const inches = Math.round((n - feet * 12) * 100) / 100;
  if (feet === 0) return `${inches}″`;
  return inches === 0 ? `${feet}′` : `${feet}′ ${inches}″`;
}

export function formatDimensions(measurement) {
  return `${formatInches(measurement.length_in)} × ${formatInches(measurement.width_in)}`;
}

export function locationAreaTotal(location) {
  return (location.features || []).reduce(
    (sum, feature) =>
      sum + (feature.measurements || []).reduce((s, m) => s + (Number(m.area_sqft) || 0), 0),
    0,
  );
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function formatMessageTime(value, now = new Date()) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const clock = date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  const sameDay = date.toDateString() === now.toDateString();
  return sameDay ? clock : `${MONTHS[date.getMonth()]} ${date.getDate()}, ${clock}`;
}

export function formatClientMoney(value) {
  return formatMoneyFromCents(toCents(value));
}

/** Calendar date from an ISO date, without shifting across the local timezone. */
export function formatClientDueDate(value) {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return "";
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
}

/** Billing is shared only when the token payload includes the billing object. */
export function clientBillingShared(view) {
  return Array.isArray(view?.billing?.invoices);
}

export function pictureCountLabel(count) {
  if (!count) return "None shared";
  return `${count} shared`;
}

export function shareStatusLabel(share) {
  return share?.is_active ? "Active" : "Not shared";
}

export function isMissingRpcError(error) {
  const text = `${error?.code || ""} ${error?.message || ""}`;
  return /PGRST202|42883|could not find the function/i.test(text);
}

/** Supabase / PostgREST errors carry the RAISE text in `message`. */
export function friendlyError(error, fallback = "Something went wrong. Please try again.") {
  const message = String(error?.message || "");
  if (isMissingRpcError(error)) return "Client View is not set up in this environment yet.";
  if (/no longer active/i.test(message)) return "This link is no longer active.";
  if (/Too many messages/i.test(message)) return "You have sent a lot of messages. Please try again in a few minutes.";
  if (/closed to new messages/i.test(message)) return "This project is closed to new messages.";
  if (/Not allowed|insufficient_privilege/i.test(message)) return "You do not have permission to do that.";
  if (/CP_OPERATIONAL_LOCK|archived/i.test(message)) return "This project is archived. Restore it to change sharing.";
  if (/Write a message/i.test(message)) return "Write a message (up to 2,000 characters).";
  return message && message.length < 140 ? message : fallback;
}
